import { useCallback, useEffect, useState } from "react";
import { Newspaper, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MultiImageField } from "@/components/MultiImageField";
import { AuthImage } from "@/components/AuthImage";
import { HeadlineCard, AnnouncementCard, TopicFilter, TOPIC_LABELS } from "@/components/NewsList";
import { NewsBoardCard } from "@/components/NewsBoardCard";

const PUBLISH_ROLES = ["admin", "vendor", "buyer"];
const EMPTY = { title: "", body: "", topic: "manufacturing", public: true, link: "", images: [] };

export default function News() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("all");
  const [feed, setFeed] = useState(null);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const canPublish = PUBLISH_ROLES.includes(user?.role);

  const loadPosts = useCallback(() => {
    api.get("/news/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    setFeed(null);
    api.get(`/news/headlines?topic=${topic}&limit=24`)
      .then(({ data }) => setFeed(data))
      .catch(() => setFeed({ items: [], unavailable: true }));
  }, [topic]);

  useEffect(loadPosts, [loadPosts]);

  const publish = async () => {
    setSaving(true);
    try {
      await api.post("/news/posts", {
        title: form.title,
        body: form.body,
        topic: form.topic,
        public: form.public,
        link: form.link || null,
        image_ids: form.images.map((i) => i.image_id),
      });
      toast.success("Published");
      setOpen(false);
      setForm(EMPTY);
      loadPosts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/news/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const shown = topic === "all" ? posts : posts.filter((p) => p.topic === topic);

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-[#B3241B]">News</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">Industry news & announcements</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Headlines on steel, freight, manufacturing, trade and safety, pulled in automatically — plus what your team has published.
          </p>
        </div>
        {canPublish && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="news-publish-open"><Plus size={16} className="mr-1.5" /> Publish an announcement</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader><DialogTitle>Publish an announcement</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="news-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="New depot opening in Pune" />
                </div>
                <div>
                  <Label>What is happening</Label>
                  <Textarea data-testid="news-form-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </div>
                <div>
                  <Label>Topic</Label>
                  <select
                    data-testid="news-form-topic"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="mt-1 w-full border border-border bg-white px-3 py-2 text-base md:text-sm"
                  >
                    {Object.entries(TOPIC_LABELS).filter(([k]) => k !== "all").map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Link (optional)</Label>
                  <Input data-testid="news-form-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" />
                </div>
                <MultiImageField testid="news-form-photos" images={form.images} max={4} onChange={(images) => setForm({ ...form, images })} />
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input data-testid="news-form-public" type="checkbox" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} />
                  Show this on our public news page too
                </label>
                <Button data-testid="news-form-submit" onClick={publish} disabled={saving} className="w-full">
                  {saving ? "Publishing…" : "Publish"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <TopicFilter value={topic} onChange={setTopic} />

      <NewsBoardCard canRefresh={user?.role === "admin"} />

      {shown.length > 0 && (
        <div className="space-y-3" data-testid="news-posts">
          {shown.map((post, i) => (
            <AnnouncementCard key={post.id} post={post} index={i} canDelete={user?.role === "admin" || post.author_id === user?.id} onDelete={remove}>
              {post.image_ids?.length > 0 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {post.image_ids.map((id) => (
                    <AuthImage key={id} imageId={id} alt={post.title} testid={`news-post-image-${id}`} className="h-24 w-32 flex-none object-cover" />
                  ))}
                </div>
              )}
              {!post.public && <p className="mt-2 text-xs text-slate-400">Workspace only</p>}
            </AnnouncementCard>
          ))}
        </div>
      )}

      {feed?.unavailable && (
        <p data-testid="news-unavailable" className="border-l-2 border-[#B3241B] bg-[#FFECEB] px-3 py-2 text-xs text-[#7A1712]">
          News sources are not reachable just now — try again in a moment.
        </p>
      )}
      {feed?.stale && (
        <p data-testid="news-stale" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-xs text-[#0F172A]">
          Showing the last headlines we had.
        </p>
      )}

      {!feed ? (
        <p className="py-12 text-center text-sm text-slate-500">Fetching the latest headlines…</p>
      ) : feed.items.length === 0 ? (
        <p data-testid="news-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
          <Newspaper size={20} className="mx-auto mb-2 text-slate-400" /> No headlines to show yet.
        </p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="news-headlines">
          {feed.items.map((item, i) => <HeadlineCard key={`${item.url}-${i}`} item={item} index={i} canResearch={["admin", "vendor", "buyer"].includes(user?.role)} />)}
        </div>
      )}
    </div>
  );
}
